import { useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Dropdown, Modal } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";

const PostPage = () => {
 /* ================= MODALS ================= */
 const [postModal, setPostModal] = useState(false);
 const [editModal, setEditModal] = useState(false);

 /* ================= DATA ================= */
 const [tasks, setTasks] = useState([]);
 const [employees, setEmployees] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [editTaskId, setEditTaskId] = useState(null);

 /* ================= FILTERS ================= */
 const [filters, setFilters] = usePersistentFilters("task_filters", {
 search: "",
 workOrder: "",
 employee: "",
 status: "",
 from: "",
 to: "",
 });

 /* ================= PAGINATION ================= */
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 });

 const limit = 12;
 const [loading, setLoading] = useState(true);

 /* ================= ADD FORM ================= */
 const [addFormData, setAddFormData] = useState({
 workOrder: "",
 assignedEmployeeId: "",
 title: "",
 description: "",
 priority: "Medium",
 estimatedHours: 2,
 requiredSkills: [],
 plannedEndDate: "",
 });

 /* ================= EDIT FORM ================= */
 const [editFormData, setEditFormData] = useState({
 workOrder: "",
 assignedEmployeeId: "",
 title: "",
 description: "",
 priority: "Medium",
 status: "",
 estimatedHours: 2,
 requiredSkills: [],
 plannedEndDate: "",
 });

 const [skillInput, setSkillInput] = useState("");

 /* ================= INITIAL LOAD ================= */
 useEffect(() => {
 fetchEmployees();
 fetchWorkOrders();
 fetchTasks(1);
 }, []);

 /* ================= FETCH ================= */

 const fetchEmployees = async () => {
 try {
 const query = filters.workOrder ? `?workOrder=${encodeURIComponent(filters.workOrder)}` : "";
 const res = await api.get(`/users/assignable-task-users${query}`);
 if (res.data.success) {
 const filtered = (res.data.data || []).filter(user => {
 const role = (user.role?.name || user.roleName || user.role || "").toLowerCase().trim();
 return role !== "client";
 });
 setEmployees(filtered);
 }
 } catch {
 swal("Error", "Failed to load employees", "error");
 }
 };

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders");
 if (res.data.success) setWorkOrders(res.data.data);
 } catch {
 swal("Error", "Failed to load work orders", "error");
 }
 };

 const fetchTasks = async (page = 1) => {
 setLoading(true);
 try {
 const query = new URLSearchParams({
 page,
 limit,
 search: filters.search,
 status: filters.status,
 employee: filters.employee,
 workOrder: filters.workOrder,
 from: filters.from,
 to: filters.to,
 }).toString();

 const res = await api.get(`/tasks?${query}`);

 if (res.data.success) {
 setTasks(res.data.data);
 setPagination({
 currentPage: page,
 totalPages: res.data.pagination?.totalPages || 1,
 });
 }
 } catch (err) {
 swal("Error", err.message, "error");
 }
 setLoading(false);
};


 /* ================= HELPERS ================= */

 const getVisualStatus = (task) => {
 const now = new Date();
 const deadline = task.plannedEndDate ? new Date(task.plannedEndDate) : null;

 if (task.status === "Completed") return "Completed";

 if (deadline && !Number.isNaN(deadline.getTime())) {
 const delayStartDate = new Date(deadline);
 delayStartDate.setDate(delayStartDate.getDate() + 1);
 delayStartDate.setHours(0, 0, 0, 0);

 if (now >= delayStartDate && task.status !== "Completed") {
 return "Delayed";
 }
 }

 return task.status || "Unassigned";
 };

 /* ================= FILTER ACTIONS ================= */

 const handleFilterChange = (e) =>
 setFilters((current) => {
 const next = { ...current, [e.target.name]: e.target.value };
 if (e.target.name === "workOrder") {
 next.employee = "";
 const query = e.target.value ? `?workOrder=${encodeURIComponent(e.target.value)}` : "";
 api.get(`/users/assignable-task-users${query}`)
 .then((res) => {
 const filtered = (res.data.data || []).filter(user => {
 const role = (user.role?.name || user.roleName || user.role || "").toLowerCase().trim();
 return role !== "client";
 });
 setEmployees(filtered);
 })
 .catch(() => swal("Error", "Failed to load employees", "error"));
 }
 return next;
 });

 const applyFilters = () => fetchTasks(1);

 const resetFilters = () => {
 setFilters({
 search: "",
 workOrder: "",
 employee: "",
 status: "",
 from: "",
 to: "",
 });
 fetchTasks(1);
 };

 /* ================= CRUD ACTIONS ================= */

 const handleDeleteClick = (id) => {
 swal({
 title: "Delete Task?",
 text: "This action cannot be undone!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (yes) => {
 if (!yes) return;
 try {
 const res = await api.delete(`/tasks/${id}`);
 if (res.data.success) {
 swal("Deleted!", "Task removed", "success");
 fetchTasks(pagination.currentPage);
 }
 } catch (err) {
 swal("Error", err.message, "error");
 }
 });
 };

 const handleEditClick = (e, task) => {
 e.preventDefault();
 setEditTaskId(task._id);
 const nextWorkOrder = task.workOrder?._id || "";

 if (nextWorkOrder) {
 api.get(`/users/assignable-task-users?workOrder=${encodeURIComponent(nextWorkOrder)}`)
 .then((res) => {
 const filtered = (res.data.data || []).filter(user => {
 const role = (user.role?.name || user.roleName || user.role || "").toLowerCase().trim();
 return role !== "client";
 });
 setEmployees(filtered);
 })
 .catch(() => swal("Error", "Failed to load employees", "error"));
 }

 setEditFormData({
 workOrder: nextWorkOrder,
 assignedEmployeeId: task.employeeId || "",

 title: task.title || "",
 description: task.description || "",
 priority: task.priority,
 status: task.status,
 estimatedHours: task.estimatedHours,
 requiredSkills: task.requiredSkills || [],
 plannedEndDate: task.plannedEndDate
 ? new Date(task.plannedEndDate).toISOString().split("T")[0]
 : "",
 });

 setEditModal(true);
 };

 const handleAddInput = (e) => {
 const { name, value } = e.target;
 setAddFormData({
 ...addFormData,
 [name]: value,
 ...(name === "workOrder" ? { assignedEmployeeId: "" } : {}),
 });
 if (name === "workOrder") {
 const query = value ? `?workOrder=${encodeURIComponent(value)}` : "";
 api.get(`/users/assignable-task-users${query}`)
 .then((res) => {
 const filtered = (res.data.data || []).filter(user => {
 const role = (user.role?.name || user.roleName || user.role || "").toLowerCase().trim();
 return role !== "admin" && role !== "client";
 });
 setEmployees(filtered);
 })
 .catch(() => swal("Error", "Failed to load employees", "error"));
 }
 };

 const handleEditInput = (e) => {
 const { name, value } = e.target;
 setEditFormData({
 ...editFormData,
 [name]: value,
 ...(name === "workOrder" ? { assignedEmployeeId: "" } : {}),
 });
 if (name === "workOrder") {
 const query = value ? `?workOrder=${encodeURIComponent(value)}` : "";
 api.get(`/users/assignable-task-users${query}`)
 .then((res) => {
 const filtered = (res.data.data || []).filter(user => {
 const role = (user.role?.name || user.roleName || user.role || "").toLowerCase().trim();
 return role !== "admin" && role !== "client";
 });
 setEmployees(filtered);
 })
 .catch(() => swal("Error", "Failed to load employees", "error"));
 }
 };

 const addSkill = () => {
 if (!skillInput.trim()) return;
 setAddFormData({
 ...addFormData,
 requiredSkills: [...addFormData.requiredSkills, skillInput.trim()],
 });
 setSkillInput("");
 };

const handleAddSubmit = async (e) => {
 e.preventDefault();

 if (!addFormData.title) {
 swal("Required", "Title is required", "warning");
 return;
 }

const payload = {
 ...addFormData,
 workOrderId: addFormData.workOrder,
 assignedEmployee: addFormData.assignedEmployeeId,
};

delete payload.workOrder;
delete payload.assignedEmployeeId;




 try {
 const res = await api.post("/tasks", payload);
 if (res.data.success) {
 swal("Success", "Task created!", "success");
 setPostModal(false);
 fetchTasks(1);
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 }
};



const handleEditSubmit = async (e) => {
 e.preventDefault();

 const payload = {
 ...editFormData,
 workOrderId: editFormData.workOrder,
 assignedEmployee: editFormData.assignedEmployeeId,
};

delete payload.workOrder;
delete payload.assignedEmployeeId;


 try {
 const res = await api.patch(`/tasks/${editTaskId}`, payload);
 if (res.data.success) {
 swal("Updated", "Task updated!", "success");
 setEditModal(false);
 fetchTasks(pagination.currentPage);
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 }
};



 /* ================= JSX ================= */

 return (
 <>
 {/* FILTER BAR */}
 <div className="card p-3 mb-4 shadow-sm">
 <div className="row g-2">
 <div className="col-md-3">
 <input
 name="search"
 className="form-control form-control-sm"
 placeholder="Search Task"
 value={filters.search}
 onChange={handleFilterChange}
 />
 </div>

 <div className="col-md-3">
 <select
 name="workOrder"
 className="form-select form-select-sm"
 value={filters.workOrder}
 onChange={handleFilterChange}
 >
 <option value="">All Work Orders</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo} - {wo.project?.projectName}

 </option>
 ))}
 </select>
 </div>

 <div className="col-md-2">
 <select
 name="employee"
 className="form-select form-select-sm"
 value={filters.employee}
 onChange={handleFilterChange}
 >
 <option value="">All Employees</option>
 {employees.map((e) => (
 <option key={e._id} value={e._id}>
 {e.name}
 </option>
 ))}
 </select>
 </div>

 <div className="col-md-2">
 <select
 name="status"
 className="form-select form-select-sm"
 value={filters.status}
 onChange={handleFilterChange}
 >
 <option value="">All Status</option>
 <option>Unassigned</option>
 <option>Assigned</option>
 <option>In Progress</option>
 <option>Completed</option>
 <option>Delayed</option>
 </select>
 </div>

 <div className="col-md-2 d-grid gap-1">
 <button className="btn btn-dark" onClick={applyFilters}>
 Go
 </button>
 <button
 className="btn btn-outline-secondary"
 onClick={resetFilters}
 >
 Reset
 </button>
 </div>
 </div>
 </div>

 {/* ADD BUTTON */}
 <div className="d-flex justify-content-end mb-3">
 <button className="btn btn-primary" onClick={() => setPostModal(true)}>
 + New Task
 </button>
 </div>

 {/* TASK GRID */}
 {loading ? (
 <div className="text-center py-5">Loading...</div>
 ) : (
 <div className="row">
 {tasks.map((task) => (
 <div key={task._id} className="col-md-4 mb-3">
 <div className="p-3 border rounded shadow-sm">
 <div className="d-flex">
 <div>
 <h6>{task.title}</h6>
 <small>{task.employeeName || "Unassigned"}</small>

 <div className="badge bg-secondary mt-1">
 {getVisualStatus(task)}
 </div>
 </div>

 <div className="ms-auto">
 <Dropdown>
 <Dropdown.Toggle as="div" className="i-false">
 <i className="fa fa-ellipsis-v"></i>
 </Dropdown.Toggle>
 <Dropdown.Menu align="end">
 <Dropdown.Item
 onClick={(e) => handleEditClick(e, task)}
 >
 Edit
 </Dropdown.Item>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDeleteClick(task._id)}
 >
 Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </div>

 <div className="mt-2 small">
 <div>WO: {task.workOrder?.woNo || "Other"}</div>


 <div>Deadline: {task.plannedEndDate}</div>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}

 {/* PAGINATION */}
{pagination.totalPages > 1 && (
 <div className="d-flex justify-content-center mt-3 gap-2">
 <button
 className="btn btn-outline-secondary btn-sm"
 disabled={pagination.currentPage === 1}
 onClick={() => fetchTasks(pagination.currentPage - 1)}
 >
 Prev
 </button>

 <span className="pt-1">
 Page {pagination.currentPage} of {pagination.totalPages}
 </span>

 <button
 className="btn btn-outline-secondary btn-sm"
 disabled={pagination.currentPage === pagination.totalPages}
 onClick={() => fetchTasks(pagination.currentPage + 1)}
 >
 Next
 </button>
 </div>
)}
{/* ADD MODAL */}
<Modal show={postModal} onHide={() => setPostModal(false)}>
 <div className="modal-header">
 <h5>Add Task</h5>
 <button className="btn-close" onClick={() => setPostModal(false)}></button>
 </div>

 <form onSubmit={handleAddSubmit}>
 <div className="modal-body">
 {/* WORK ORDER */}
 <label className="mt-1">Work Order</label>
 <select
 name="workOrder"
 className="form-select"
 value={addFormData.workOrder}
 onChange={handleAddInput}
 >
 <option value="">Other / General</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo} - {wo.project.projectName}

 </option>
 ))}
 </select>

 {/* EMPLOYEE */}
 <label className="mt-2">Assign Employee</label>
 <select
 name="assignedEmployeeId"
 className="form-select"
 value={addFormData.assignedEmployeeId}
 onChange={handleAddInput}
 >
 <option value="">-- Select --</option>
 {employees.map((emp) => (
 <option key={emp._id} value={emp._id}>
 {emp.name}
 </option>
 ))}
 </select>

 <label className="mt-2">Title *</label>
 <input
 type="text"
 name="title"
 className="form-control"
 value={addFormData.title}
 onChange={handleAddInput}
 required
 />

 <label className="mt-2">Description</label>
 <textarea
 name="description"
 className="form-control"
 rows="2"
 value={addFormData.description}
 onChange={handleAddInput}
 />

 <label className="mt-2">Priority</label>
 <select
 name="priority"
 className="form-select"
 value={addFormData.priority}
 onChange={handleAddInput}
 >
 <option>Low</option>
 <option>Medium</option>
 <option>High</option>
 <option>Critical</option>
 </select>

 <label className="mt-2">Estimated Hours</label>
 <input
 type="number"
 name="estimatedHours"
 className="form-control"
 min="1"
 value={addFormData.estimatedHours}
 onChange={handleAddInput}
 />

 <label className="mt-2">Deadline *</label>
 <input
 type="date"
 name="plannedEndDate"
 className="form-control"
 value={addFormData.plannedEndDate}
 onChange={handleAddInput}
 required
 />

 {/* SKILLS */}
 <label className="mt-2">Required Skills (Optional)</label>
 <div className="d-flex gap-2">
 <input
 value={skillInput}
 className="form-control"
 placeholder="e.g. Electrical"
 onChange={(e) => setSkillInput(e.target.value)}
 />
 <button type="button" className="btn btn-success" onClick={addSkill}>
 Add
 </button>
 </div>

 {addFormData.requiredSkills.length > 0 && (
 <div className="mt-2">
 {addFormData.requiredSkills.map((s, i) => (
 <span key={i} className="badge bg-dark me-1">
 {s}
 </span>
 ))}
 </div>
 )}
 </div>

 <div className="modal-footer">
 <button className="btn btn-primary" type="submit">
 Save
 </button>
 <button
 type="button"
 className="btn btn-danger"
 onClick={() => setPostModal(false)}
 >
 Cancel
 </button>
 </div>
 </form>
</Modal>
{/* EDIT MODAL */}
<Modal show={editModal} onHide={() => setEditModal(false)}>
 <div className="modal-header">
 <h5>Edit Task</h5>
 <button className="btn-close" onClick={() => setEditModal(false)}></button>
 </div>

 <form onSubmit={handleEditSubmit}>
 <div className="modal-body">
 {/* WORK ORDER */}
 <label className="mt-1">Work Order</label>
 <select
 name="workOrder"
 value={editFormData.workOrder}
 className="form-select"
 onChange={handleEditInput}
 >
 <option value="">Other / General</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo} - {wo.project?.projectName}


 </option>
 ))}
 </select>

 <label className="mt-2">Assign Employee</label>
 <select
 name="assignedEmployeeId"
 value={editFormData.assignedEmployeeId}
 className="form-select"
 onChange={handleEditInput}
 >
 <option value="">-- Select --</option>
 {employees.map((emp) => (
 <option key={emp._id} value={emp._id}>
 {emp.name}
 </option>
 ))}
 </select>

 <label className="mt-2">Title</label>
 <input
 type="text"
 name="title"
 value={editFormData.title}
 className="form-control"
 onChange={handleEditInput}
 />

 <label className="mt-2">Description</label>
 <textarea
 name="description"
 value={editFormData.description}
 className="form-control"
 rows="2"
 onChange={handleEditInput}
 />

 <label className="mt-2">Priority</label>
 <select
 name="priority"
 value={editFormData.priority}
 className="form-select"
 onChange={handleEditInput}
 >
 <option>Low</option>
 <option>Medium</option>
 <option>High</option>
 <option>Critical</option>
 </select>

 <label className="mt-2">Status</label>
 <select
 name="status"
 value={editFormData.status}
 className="form-select"
 onChange={handleEditInput}
 >
 <option>Unassigned</option>
 <option>Assigned</option>
 <option>In Progress</option>
 <option>Completed</option>
 <option>Delayed</option>
 </select>

 <label className="mt-2">Estimated Hours</label>
 <input
 type="number"
 name="estimatedHours"
 value={editFormData.estimatedHours}
 className="form-control"
 onChange={handleEditInput}
 />

 <label className="mt-2">Deadline</label>
 <input
 type="date"
 name="plannedEndDate"
 value={editFormData.plannedEndDate}
 className="form-control"
 onChange={handleEditInput}
 />

 <label className="mt-2">Skills</label>
 {editFormData.requiredSkills.map((s, i) => (
 <span key={i} className="badge bg-dark me-1">
 {s}
 </span>
 ))}
 </div>

 <div className="modal-footer">
 <button className="btn btn-primary" type="submit">
 Save
 </button>
 <button
 type="button"
 className="btn btn-danger"
 onClick={() => setEditModal(false)}
 >
 Cancel
 </button>
 </div>
 </form>
</Modal>


 </>
 );
};

export default PostPage;
